import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import type { GuideStep } from '../helpGuide'
import { placeTour, type TourRect } from '../tourPlacement'

function viewportRect(): TourRect {
  const viewport = window.visualViewport
  return { left: viewport?.offsetLeft || 0, top: viewport?.offsetTop || 0,
    width: viewport?.width || window.innerWidth, height: viewport?.height || window.innerHeight }
}
function visibleTarget(step: GuideStep) {
  for (const selector of [step.selector, step.fallbackSelector].filter(Boolean)) {
    for (const node of document.querySelectorAll<HTMLElement>(selector!)) {
      const rect = node.getBoundingClientRect()
      if (rect.width && rect.height && getComputedStyle(node).visibility !== 'hidden' && !node.closest('.spotlight-tour'))
        return { node, fallback: selector === step.fallbackSelector }
    }
  }
  return null
}
function targetRect(node: HTMLElement, viewport: TourRect): TourRect | null {
  const box = node.getBoundingClientRect()
  let left = Math.max(viewport.left + 5, box.left - 7), top = Math.max(viewport.top + 5, box.top - 7)
  let right = Math.min(viewport.left + viewport.width - 5, box.right + 7)
  let bottom = Math.min(viewport.top + viewport.height - 5, box.bottom + 7)
  // A spotlight must never suggest that content behind a scrolling panel's edge is visible.
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent), rect = parent.getBoundingClientRect()
    if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) { top = Math.max(top, rect.top); bottom = Math.min(bottom, rect.bottom) }
    if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) { left = Math.max(left, rect.left); right = Math.min(right, rect.right) }
  }
  return right > left && bottom > top ? { left, top, width: right - left, height: bottom - top } : null
}
function makeRoomForCard(node: HTMLElement, viewport: TourRect, height: number) {
  const target = targetRect(node, viewport)
  if (!target) return
  const position = placeTour(target, viewport, height)
  if (position.availableHeight >= height || !['top', 'bottom'].includes(position.side)) return
  let top = viewport.top + 12, bottom = viewport.top + viewport.height - 12
  const scrollers: HTMLElement[] = []
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent), bounds = parent.getBoundingClientRect()
    if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
      top = Math.max(top, bounds.top); bottom = Math.min(bottom, bounds.bottom)
    }
    if (/(auto|scroll)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight) scrollers.push(parent)
  }
  // Move the control towards a panel edge before making its explanation scroll.
  // Keep the entire visible control in the panel, and never scroll past its limits.
  const below = position.side === 'bottom'
  const space = below ? target.top - top : bottom - target.top - target.height
  let remaining = Math.min(height - position.availableHeight, Math.max(0, space))
  for (const scroller of scrollers) {
    const amount = Math.min(remaining, below ? scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop : scroller.scrollTop)
    if (amount < 1) continue
    scroller.scrollBy({ top: below ? amount : -amount, behavior: 'instant' })
    remaining -= amount
    if (remaining < 1) break
  }
}
export function GuidedTour({ title, steps, index, busy, error, onStep, onFinish, onLeave }: {
  title: string
  steps: GuideStep[]
  index: number
  busy: boolean
  error?: string
  onStep: (index: number) => void
  onFinish: (status: 'completed' | 'dismissed') => void
  onLeave: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const card = useRef<HTMLDivElement>(null)
  const copy = useRef<HTMLDivElement>(null)
  const next = useRef<HTMLButtonElement>(null)
  const savedScroll = useRef(new Map<HTMLElement, { top: number; left: number }>())
  const maskId = useId().replace(/:/g, '')
  const headingId = useId(), bodyId = useId()
  const step = steps[index]
  const [geometry, setGeometry] = useState<{ viewport: TourRect; target: TourRect | null; cardHeight: number; fallback: boolean }>(
    () => ({ viewport: viewportRect(), target: null, cardHeight: 240, fallback: false }))
  useLayoutEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const modal = dialog.current
    modal?.showModal()
    next.current?.focus({ preventScroll: true })
    return () => {
      modal?.close()
      for (const [node, scroll] of savedScroll.current) if (node.isConnected) node.scrollTo({ ...scroll, behavior: 'instant' })
      const underneath = [...document.querySelectorAll<HTMLDialogElement>('dialog[open]')].at(-1)
      const focus = previous?.isConnected && previous !== document.body && (!underneath || underneath.contains(previous)) ? previous :
        underneath?.querySelector<HTMLElement>('button:not(:disabled), [tabindex="0"]') ||
        document.querySelector<HTMLElement>('[aria-label="Help and tours"]')
      focus?.focus({ preventScroll: true })
    }
  }, [])
  useLayoutEffect(() => {
    if (copy.current) copy.current.scrollTop = error ? copy.current.scrollHeight : 0
    let target: HTMLElement | null = null, frame = 0, viewportSize = ''
    const observer = new ResizeObserver(() => schedule())
    if (card.current) observer.observe(card.current)
    const measure = () => {
      const found = visibleTarget(step)
      if (found?.node !== target) {
        if (target) observer.unobserve(target)
        target = found?.node || null
        if (target) {
          for (let node: HTMLElement | null = target; node; node = node.parentElement) {
            if (!savedScroll.current.has(node) && (node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth))
              savedScroll.current.set(node, { top: node.scrollTop, left: node.scrollLeft })
          }
          target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' })
          observer.observe(target)
        }
      }
      const viewport = viewportRect()
      const size = `${viewport.left},${viewport.top},${viewport.width},${viewport.height}`
      if (size !== viewportSize) {
        target?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' })
        viewportSize = size
      }
      // Measure the unconstrained content so a short viewport does not cause a placement loop.
      const cardHeight = (card.current?.scrollHeight || 240) + (card.current ? card.current.offsetHeight - card.current.clientHeight : 0) + Math.max(0,
        (copy.current?.scrollHeight || 0) - (copy.current?.clientHeight || 0))
      if (target) makeRoomForCard(target, viewport, cardHeight)
      const value = { viewport, target: target ? targetRect(target, viewport) : null, cardHeight, fallback: !!found?.fallback }
      setGeometry(old => JSON.stringify(old) === JSON.stringify(value) ? old : value)
    }
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure) }
    measure()
    const mutations = new MutationObserver(records => {
      if (records.some(record => !(record.target instanceof Element) || !record.target.closest('.spotlight-tour'))) schedule()
    })
    mutations.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    window.visualViewport?.addEventListener('resize', schedule)
    window.visualViewport?.addEventListener('scroll', schedule)
    return () => {
      cancelAnimationFrame(frame); observer.disconnect(); mutations.disconnect()
      window.removeEventListener('resize', schedule); window.removeEventListener('scroll', schedule, true)
      window.visualViewport?.removeEventListener('resize', schedule)
      window.visualViewport?.removeEventListener('scroll', schedule)
    }
  }, [step, error])
  const position = placeTour(geometry.target, geometry.viewport, geometry.cardHeight)
  const target = geometry.target
  return createPortal(<dialog ref={dialog} className="spotlight-tour" aria-label={'Tour: ' + title}
    aria-describedby={bodyId} onCancel={event => { event.preventDefault(); if (!busy) onFinish('dismissed') }}
    onKeyDown={event => {
      if (event.key === 'Tab') {
        const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), [tabindex="0"]')]
        const first = controls[0], last = controls[controls.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus({ preventScroll: true }) }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus({ preventScroll: true }) }
        return
      }
      if (busy || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return
      event.preventDefault()
      if (event.key === 'ArrowLeft' && index > 0) onStep(index - 1)
      if (event.key === 'ArrowRight' && index < steps.length - 1) onStep(index + 1)
    }}>
    <svg className="spotlight-tour-mask" width="100%" height="100%" aria-hidden="true">
      <defs><mask id={maskId}><rect width="100%" height="100%" fill="white" />
        {target && <rect x={target.left} y={target.top} width={target.width} height={target.height} rx="8" fill="black" />}
      </mask></defs>
      <rect width="100%" height="100%" fill="var(--tour-scrim)" mask={'url(#' + maskId + ')'} />
      {target && <rect className="spotlight-tour-ring" x={target.left} y={target.top} width={target.width} height={target.height} rx="8" />}
    </svg>
    <div ref={card} className="spotlight-tour-card" data-placement={position.side} aria-labelledby={headingId}
      style={{ left: position.left, top: position.top, width: position.width, maxHeight: position.maxHeight }}>
      {target && position.arrow !== null && <span className="spotlight-tour-arrow" aria-hidden="true"
        style={position.side === 'top' || position.side === 'bottom' ? { left: position.arrow } : { top: position.arrow }} />}
      <div className="spotlight-tour-heading"><span className="eyebrow">{title}</span>
        <button type="button" className="icon-button" aria-label="Close tour" disabled={busy} onClick={() => onFinish('dismissed')}><X size={17} /></button>
      </div>
      <div ref={copy} className="spotlight-tour-copy" tabIndex={0} aria-live="polite" aria-atomic="true">
        <span className="spotlight-tour-count">Step {index + 1} of {steps.length}</span>
        <h2 id={headingId}>{step.title}</h2>
        <p id={bodyId}>{geometry.fallback && step.fallbackText ? step.fallbackText : step.text}</p>
        {error && <div className="spotlight-tour-error" role="alert"><p>{error}</p>
          <p>Your tour preference hasn’t been saved. You can retry below or <button type="button" className="text-button"
            disabled={busy} onClick={onLeave}>close for now</button>.</p>
        </div>}
      </div>
      <footer className="spotlight-tour-controls">
        <button type="button" className="text-button" disabled={busy} onClick={() => onFinish('dismissed')}>Skip tour</button>
        <span />
        <button type="button" className="button" disabled={busy || index === 0} onClick={() => onStep(index - 1)}><ArrowLeft size={13} />Back</button>
        <button ref={next} type="button" className="button button--primary" disabled={busy}
          onClick={() => index === steps.length - 1 ? onFinish('completed') : onStep(index + 1)}>
          {busy ? 'Saving…' : index === steps.length - 1 ? 'Got it' : 'Next'}<ArrowRight size={13} />
        </button>
      </footer>
    </div>
  </dialog>, document.body)
}
